import {
  createContext,
  useContext,
  useState,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  MutableRefObject,
} from "react";
import { fetchFastApi } from "../api/fastapi";
import { toast } from "sonner";
import { useAudit } from "./auditContext";
import { SessionRun, SessionRunSchema } from "../models/audit/sessionRun";
import { EventRun, EventRunSchema } from "../models/audit/eventRun";
import { parseModelArrayTolerant } from "../lib/parseModelArray";
import { runLogEntries } from "../lib/sessionRunUtils";
import { z } from "zod";

// Envelope of `GET /controls/v2/control-session/{id}/runs`. The shape must be
// right — a malformed envelope throws into the fetcher's error path like any
// transport failure — but rows parse per-element below so one bad run degrades
// only itself (MS-3366).
const controlSessionRunsResponseSchema = z.object({
  sessionRuns: z.array(z.unknown()),
  eventRuns: z.array(z.unknown()),
});

// Re-export utility functions from sessionRunUtils for convenience
/* eslint-disable react-refresh/only-export-components */
export {
  getLatestCheckResult,
  isSupportColumnLocked,
  isEvalColumnLocked,
  isColumnLocked,
  createSessionRun,
  createPlaceholderSessionRun,
  createSessionRunsForUnlockedCells,
  getHighlightCellClassName,
} from "../lib/sessionRunUtils";
export type { HighlightEdges } from "../lib/sessionRunUtils";
/* eslint-enable react-refresh/only-export-components */

/** Row-wide wildcard: a run with `targetColumnIds === null` targets every
 * column for its `sourceRowId`. Resolving that against real column ids would
 * require the table shell, which this context does not hold, so the raw
 * wildcard is stored instead — `WorkPaperTableV2` (which does know its
 * columns) treats `columnId === "*"` as a row-wide match. */
const ALL_COLUMNS_WILDCARD = "*";

/** Delays (ms), after the immediate fetch, for the session-runs refetch burst
 * a 202-async run-triggering POST fires (see `burstFetchSessionRuns`). */
const SESSION_RUNS_BURST_DELAYS_MS = [1500, 4000, 8000];

/** How long a highlight sits fully visible before it starts to fade. */
const HIGHLIGHT_VISIBLE_MS = 4000;
/** How long the fade-out phase lasts before the selection is actually
 * cleared. Deliberately a little longer than the `duration-700` CSS
 * transition the cells fade with (see `DataCell`/`CheckDecision`), so the
 * transition has finished — the cell already reads as fully transparent —
 * before the highlight classes disappear underneath it. */
const HIGHLIGHT_FADE_MS = 900;
/** Duration of the manual scroll-into-view animation (see
 * `scrollElementIntoView`), shared by both axes. */
const SCROLL_ANIMATION_DURATION_MS = 350;

/** A cell targeted for the highlight. `columnId` is either a real
 * column id or {@link ALL_COLUMNS_WILDCARD}. */
export interface HighlightedCells {
  sourceRowId: string;
  columnId: string;
}

const resolveHighlightCells = (runs: SessionRun[]): HighlightedCells[] =>
  runs.flatMap((run) =>
    run.targetColumnIds === null
      ? [{ sourceRowId: run.sourceRowId, columnId: ALL_COLUMNS_WILDCARD }]
      : run.targetColumnIds.map((columnId) => ({
          sourceRowId: run.sourceRowId,
          columnId,
        })),
  );

/** The two axes the highlight scroll-into-view can animate. */
type ScrollAxis = "horizontal" | "vertical";

/** In-flight scroll animation frame ids, one per axis, so a fresh highlight
 * or an unmount can cancel whatever's still running on either. A single ref
 * shared across both axes rather than two separate ones — one thing to
 * cancel, not two things to remember to cancel together. */
interface ScrollAnimationRafIds {
  horizontal: number | null;
  vertical: number | null;
}

/** Nearest scrollable ancestor of `element` along `axis` — walks up
 * `parentElement`s checking computed `overflow-x`/`overflow-y` plus real
 * overflow (`scrollWidth`/`scrollHeight` beyond the client box). For the
 * vertical axis, falls back to `document.scrollingElement` (the page's own
 * scroller) when no closer ancestor qualifies — this app has no fixed-height
 * vertical scroll container around the workpaper grid, so the page itself is
 * what actually moves. The horizontal axis has no such page-level fallback
 * (the page never scrolls sideways here): if no ancestor qualifies, there's
 * nothing to scroll and callers should skip the horizontal axis entirely. */
const findScrollAncestor = (
  element: Element,
  axis: ScrollAxis,
): HTMLElement | null => {
  let node = element.parentElement;
  while (node) {
    const style = window.getComputedStyle(node);
    const scrolls =
      axis === "horizontal"
        ? (style.overflowX === "auto" || style.overflowX === "scroll") &&
          node.scrollWidth > node.clientWidth
        : (style.overflowY === "auto" || style.overflowY === "scroll") &&
          node.scrollHeight > node.clientHeight;
    if (scrolls) return node;
    node = node.parentElement;
  }
  if (axis === "vertical" && document.scrollingElement instanceof HTMLElement) {
    return document.scrollingElement;
  }
  return null;
};

/** True when `element`'s full extent along `axis` already sits inside
 * `container`'s visible viewport on that axis, so scrolling would be
 * needless motion. */
const isFullyVisible = (
  element: Element,
  container: HTMLElement,
  axis: ScrollAxis,
): boolean => {
  const elementRect = element.getBoundingClientRect();
  const containerRect = container.getBoundingClientRect();
  return axis === "horizontal"
    ? elementRect.left >= containerRect.left &&
        elementRect.right <= containerRect.right
    : elementRect.top >= containerRect.top &&
        elementRect.bottom <= containerRect.bottom;
};

/** Eases `t` (0..1) in-out — shapes the scroll animations below so they
 * accelerate then decelerate instead of moving at a constant rate. */
const easeInOutQuad = (t: number): number =>
  t < 0.5 ? 2 * t * t : 1 - (-2 * t + 2) ** 2 / 2;

/** Centers `element` along `axis` within `container` via a manual
 * `requestAnimationFrame` loop that writes `container.scrollLeft` /
 * `scrollTop` every frame, rather than the browser-native
 * `Element.scrollIntoView` / `Element.scrollTo({ behavior: "smooth" })`.
 *
 * Neither native option survives here, for two separate reasons discovered
 * across two rounds of live verification:
 *
 * - A native *smooth* scroll gets canceled mid-flight: `WorkPaperTableV2`
 *   mirrors `bodyScrollRef`'s `scrollLeft` onto its header/bottom-scrollbar
 *   sibling containers on every `scroll` event with no origin guard (its
 *   `syncScroll`, WorkPaperTableV2.tsx:~1213-1224). Each of those writes
 *   fires its own `scroll` event that echoes back and rewrites
 *   `bodyScrollRef.scrollLeft` with an async-stale value, canceling the
 *   in-flight animation before it finishes.
 * - `scrollIntoView`'s `inline`/`block` options aren't a hard off-switch:
 *   even `inline: "nearest"` performs a minor horizontal correction when the
 *   matched element is partially out of view on that axis, which leaked a
 *   horizontal nudge into wildcard (row-wide) selections that should only
 *   ever scroll vertically.
 *
 * A per-frame imperative write sidesteps both: it simply overwrites any
 * echo every ~16ms so the sync mirrors each frame like ordinary
 * user-driven scroll input, and it's only ever invoked for the axis the
 * caller explicitly asked for — nothing here can nudge an axis on its own
 * initiative.
 *
 * `rafIdsRef` holds the in-flight animation's frame id for `axis` so a
 * fresh call (a new highlight) or an unmount can cancel a stale run before
 * starting or discarding this one. */
const scrollElementIntoView = (
  element: Element,
  container: HTMLElement,
  axis: ScrollAxis,
  rafIdsRef: MutableRefObject<ScrollAnimationRafIds>,
): void => {
  const isHorizontal = axis === "horizontal";
  const priorRafId = rafIdsRef.current[axis];
  if (priorRafId !== null) {
    cancelAnimationFrame(priorRafId);
    rafIdsRef.current[axis] = null;
  }
  const elementRect = element.getBoundingClientRect();
  const containerRect = container.getBoundingClientRect();
  const elementStart = isHorizontal ? elementRect.left : elementRect.top;
  const elementSize = isHorizontal ? elementRect.width : elementRect.height;
  const containerStart = isHorizontal ? containerRect.left : containerRect.top;
  const containerSize = isHorizontal
    ? container.clientWidth
    : container.clientHeight;
  const startScroll = isHorizontal ? container.scrollLeft : container.scrollTop;
  const maxScroll = isHorizontal
    ? container.scrollWidth - container.clientWidth
    : container.scrollHeight - container.clientHeight;
  const targetScroll =
    elementStart -
    containerStart +
    startScroll -
    containerSize / 2 +
    elementSize / 2;
  const clampedTarget = Math.max(0, Math.min(targetScroll, maxScroll));
  const distance = clampedTarget - startScroll;
  if (distance === 0) return;
  const startTime = performance.now();
  const animateFrame = (now: number): void => {
    const progress = Math.min(
      (now - startTime) / SCROLL_ANIMATION_DURATION_MS,
      1,
    );
    const value = startScroll + distance * easeInOutQuad(progress);
    if (isHorizontal) {
      container.scrollLeft = value;
    } else {
      container.scrollTop = value;
    }
    if (progress < 1) {
      rafIdsRef.current[axis] = requestAnimationFrame(animateFrame);
    } else {
      rafIdsRef.current[axis] = null;
    }
  };
  rafIdsRef.current[axis] = requestAnimationFrame(animateFrame);
};

/** Cancels any in-flight scroll animation on either axis. */
const cancelScrollAnimations = (
  rafIdsRef: MutableRefObject<ScrollAnimationRafIds>,
): void => {
  if (rafIdsRef.current.horizontal !== null) {
    cancelAnimationFrame(rafIdsRef.current.horizontal);
    rafIdsRef.current.horizontal = null;
  }
  if (rafIdsRef.current.vertical !== null) {
    cancelAnimationFrame(rafIdsRef.current.vertical);
    rafIdsRef.current.vertical = null;
  }
};

interface SessionRunsContextType {
  sessionRuns: SessionRun[];
  errorRuns: SessionRun[];
  eventRuns: EventRun[];
  loading: boolean;
  highlightedCells: HighlightedCells[];
  /** True once a visible highlight has entered its fade-out phase. Cells
   * read this alongside `highlightedCells` to swap from the solid highlight
   * colors to transparent, animated by their own `duration-700` transition
   * (see `DataCell`/`CheckDecision`) — this flag doesn't itself animate
   * anything, it just flips the target colors the CSS transition runs
   * toward. Never true after an immediate exit (toggle-off, replace,
   * Escape, grid click) — those clear straight to empty, no fade. */
  isHighlightFading: boolean;
  addSessionRun: (sessionRun: SessionRun) => void;
  addSessionRuns: (sessionRuns: SessionRun[]) => void;
  removeSessionRun: (sessionRunId: string) => void;
  fetchSessionRuns: (controlSessionId: string) => Promise<void>;
  /** Immediate {@link fetchSessionRuns} plus a short burst of delayed
   * refetches. A 202-async run-triggering POST (e.g. generate-work-paper)
   * returns before the backend's background dispatch has materialized its
   * rows, so a single fetch right after the POST resolves can race ahead of
   * that and come back empty — the burst covers the gap without needing to
   * know exactly when dispatch completes. Starting a new burst cancels any
   * burst still running (see {@link cancelSessionRunsBurst}). */
  burstFetchSessionRuns: (controlSessionId: string) => void;
  /** Cancels any pending burst timers AND supersedes any in-flight
   * `fetchSessionRuns`, whose response is then discarded — so stale pre-stop
   * data can't land after a stop's own refetch and repaint rows as still
   * running. Called by the stop flow before its post-stop refetch, and by
   * the provider's own unmount cleanup. */
  cancelSessionRunsBurst: () => void;
  getSessionRunsForSession: (auditSessionId: string) => SessionRun[];
  getSessionRunsForEventRun: (eventRunId: string) => SessionRun[];
  /** Seeds the highlight set directly (e.g. a stop response's
   * `stoppedScope`). Always replaces the current selection, clears the
   * toggle-source tracking, and (re)starts the visible→fade→clear timer
   * chain, so a subsequent identical call replaces again rather than
   * toggling off. */
  highlightCells: (cells: HighlightedCells[]) => void;
  /** Highlights every cell targeted by every session run under an event run,
   * resolving `targetColumnIds === null` to the row-wide wildcard, and
   * starts the visible→fade→clear timer chain. Calling this again with the
   * *same* `eventRunId` that produced the current selection clears the
   * highlight immediately instead (toggle-off, no fade); a different id
   * replaces it and restarts the chain. */
  highlightCellsForEventRun: (eventRunId: string) => void;
  /** Highlights the cells targeted by the Run Log entry a session run names:
   * the run itself and any analytics row folded into its entry (see
   * `runLogEntries`). Toggles off immediately on a repeat call with the same
   * `sessionRunId`, mirroring {@link highlightCellsForEventRun}. */
  highlightCellsForSessionRun: (sessionRunId: string) => void;
  /** Clears the highlight set immediately (no fade) and cancels any
   * in-flight fade timers. Exit triggers: a grid click, Escape, or a
   * toggle-off re-click (see above) — but never a popover close or a
   * `sessionRuns` refresh. */
  clearHighlight: () => void;
}

const SessionRunsContext = createContext<SessionRunsContextType | undefined>(
  undefined,
);

export function SessionRunsProvider({ children }: { children: ReactNode }) {
  const [sessionRuns, setSessionRuns] = useState<SessionRun[]>([]);
  const [errorRuns, setErrorRuns] = useState<SessionRun[]>([]);
  const [eventRuns, setEventRuns] = useState<EventRun[]>([]);
  const [loading, setLoading] = useState(false);
  const [highlightedCells, setHighlightedCells] = useState<HighlightedCells[]>(
    [],
  );
  const [isHighlightFading, setIsHighlightFading] = useState(false);
  // Tracks which id (namespaced `event:<id>` / `session:<id>`) produced the
  // current highlight selection, so a repeat call with the same id toggles
  // it off instead of replacing it. A ref, not state — it's only read inside
  // the callbacks below and never rendered. Reset to null by any clear or by
  // the generic `highlightCells` seeding path.
  const highlightSourceIdRef = useRef<string | null>(null);
  // The visible→fade→clear timer chain for the current highlight. Both
  // refs, not state — purely internal scheduling, never rendered directly.
  const highlightFadeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const highlightClearAfterFadeTimerRef = useRef<ReturnType<
    typeof setTimeout
  > | null>(null);
  // In-flight scroll-into-view animation frames, one per axis (see
  // `scrollElementIntoView`).
  const scrollRafIdsRef = useRef<ScrollAnimationRafIds>({
    horizontal: null,
    vertical: null,
  });

  // Monotonic id for `fetchSessionRuns` requests: a response only commits
  // its state while it is still the newest request, so a slow response
  // issued before a stop (or before a fresher fetch) can't land afterwards
  // and overwrite fresher state with stale runs. Bumped by every fetch and
  // by `cancelSessionRunsBurst`.
  const fetchRequestIdRef = useRef(0);
  // In-flight timers for the post-POST refetch burst (see
  // `burstFetchSessionRuns`), so a second trigger, a stop, or unmount
  // cancels any burst still running rather than letting bursts interleave.
  const burstTimersRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  // Read from the audit context rather than threading a kind through every
  // caller — `fetchSessionRuns` has a dozen call sites and none of them care
  // which entity the session hangs off. Safe because this provider is only
  // mounted inside `AuditProvider` (see `AuditWorkPaperViewV2WithProvider`),
  // and `useAudit` throws rather than defaulting if that ever stops being true.
  const { sessionKind } = useAudit();

  const fetchSessionRuns = useCallback(
    async (controlSessionId: string) => {
      if (!controlSessionId) return;
      const requestId = ++fetchRequestIdRef.current;
      setLoading(true);
      try {
        // Both routers answer the same {eventRuns, sessionRuns} shape;
        // `sessionKind` picks which one. The control path answered 200 with
        // empty arrays for a work-step session, which the UI rendered as
        // "never run" rather than as an error.
        const response = await fetchFastApi({
          method: "GET",
          path:
            sessionKind === "work_step"
              ? `/work-steps/work-step-session/${controlSessionId}/runs`
              : `/controls/v2/control-session/${controlSessionId}/runs`,
        });

        // Superseded while in flight (a newer fetch started, or
        // `cancelSessionRunsBurst` ran) — this response is stale, drop it.
        if (requestId !== fetchRequestIdRef.current) return;

        // Per-row tolerant parse: a single malformed run (or an enum value
        // this build doesn't know) degrades that row only — a whole-array
        // `.parse` here used to throw, freezing every cell at the last good
        // snapshot and re-toasting on every 5s poll tick (MS-3366).
        const envelope = controlSessionRunsResponseSchema.parse(response);
        const allSessionRuns = parseModelArrayTolerant(
          SessionRunSchema,
          envelope.sessionRuns,
          "session run",
        );
        const parsedEventRuns = parseModelArrayTolerant(
          EventRunSchema,
          envelope.eventRuns,
          "event run",
        );

        // Store all session runs without filtering
        setSessionRuns(allSessionRuns);
        setEventRuns(parsedEventRuns);
        // Clear error runs since we're not filtering anymore
        setErrorRuns([]);
      } catch (error) {
        // A superseded request's failure is as stale as its data would be.
        if (requestId !== fetchRequestIdRef.current) return;
        console.error("Error fetching session runs:", error);
        // Stable toast id: the 5s poller retries this fetch, and an id-less
        // toast would stack a new copy on every failing tick (MS-3366).
        toast.error("Failed to fetch session runs", {
          id: "fetch-session-runs-failed",
        });
      } finally {
        // The newest request owns the loading flag; a superseded one leaves it
        // to whichever call superseded it.
        if (requestId === fetchRequestIdRef.current) setLoading(false);
      }
    },
    [sessionKind],
  );

  const cancelSessionRunsBurst = useCallback(() => {
    for (const timer of burstTimersRef.current) {
      clearTimeout(timer);
    }
    burstTimersRef.current = [];
    // Supersede any in-flight fetch too: bumping the id makes its response
    // stale before it can commit (see `fetchSessionRuns`).
    fetchRequestIdRef.current += 1;
    setLoading(false);
  }, []);

  const burstFetchSessionRuns = useCallback(
    (controlSessionId: string) => {
      cancelSessionRunsBurst();
      void fetchSessionRuns(controlSessionId);
      burstTimersRef.current = SESSION_RUNS_BURST_DELAYS_MS.map((delay) =>
        setTimeout(() => {
          void fetchSessionRuns(controlSessionId);
        }, delay),
      );
    },
    [cancelSessionRunsBurst, fetchSessionRuns],
  );

  const addSessionRun = useCallback((sessionRun: SessionRun) => {
    setSessionRuns((prev) => {
      // Check if this run already exists
      const existingIndex = prev.findIndex((r) => r.id === sessionRun.id);
      if (existingIndex >= 0) {
        // Update existing run
        return [
          ...prev.slice(0, existingIndex),
          sessionRun,
          ...prev.slice(existingIndex + 1),
        ];
      }
      // Append new run
      return [...prev, sessionRun];
    });
  }, []);

  const addSessionRuns = useCallback((newSessionRuns: SessionRun[]) => {
    setSessionRuns((prev) => {
      const existingIds = new Set(prev.map((r) => r.id));
      const runsToAdd = newSessionRuns.filter((r) => !existingIds.has(r.id));
      return [...prev, ...runsToAdd];
    });
  }, []);

  const removeSessionRun = useCallback((sessionRunId: string) => {
    setSessionRuns((prev) => prev.filter((r) => r.id !== sessionRunId));
  }, []);

  const getSessionRunsForSession = useCallback(
    (auditSessionId: string) => {
      return sessionRuns.filter((r) => r.sessionId === auditSessionId);
    },
    [sessionRuns],
  );

  const getSessionRunsForEventRun = useCallback(
    (eventRunId: string) => {
      return sessionRuns.filter((r) => r.eventRunId === eventRunId);
    },
    [sessionRuns],
  );

  // Cancels the visible→fade→clear timer chain without touching
  // `highlightedCells`/`isHighlightFading` state itself — callers reset
  // whichever of those they need alongside this.
  const cancelHighlightFadeTimers = useCallback(() => {
    if (highlightFadeTimerRef.current) {
      clearTimeout(highlightFadeTimerRef.current);
      highlightFadeTimerRef.current = null;
    }
    if (highlightClearAfterFadeTimerRef.current) {
      clearTimeout(highlightClearAfterFadeTimerRef.current);
      highlightClearAfterFadeTimerRef.current = null;
    }
  }, []);

  // Starts (or restarts) the visible→fade→clear chain for a freshly (re)set
  // highlight: stays solid for `HIGHLIGHT_VISIBLE_MS`, then fades for
  // `HIGHLIGHT_FADE_MS`, then clears. Cancels any chain already in flight
  // first, so the newest highlight always wins.
  const scheduleHighlightFade = useCallback(() => {
    cancelHighlightFadeTimers();
    setIsHighlightFading(false);
    highlightFadeTimerRef.current = setTimeout(() => {
      highlightFadeTimerRef.current = null;
      setIsHighlightFading(true);
      highlightClearAfterFadeTimerRef.current = setTimeout(() => {
        highlightClearAfterFadeTimerRef.current = null;
        highlightSourceIdRef.current = null;
        setIsHighlightFading(false);
        setHighlightedCells([]);
      }, HIGHLIGHT_FADE_MS);
    }, HIGHLIGHT_VISIBLE_MS);
  }, [cancelHighlightFadeTimers]);

  // Immediate exit — no fade. Used by toggle-off, Escape, and grid clicks.
  const clearHighlight = useCallback(() => {
    cancelHighlightFadeTimers();
    highlightSourceIdRef.current = null;
    setIsHighlightFading(false);
    setHighlightedCells([]);
  }, [cancelHighlightFadeTimers]);

  const highlightCells = useCallback(
    (cells: HighlightedCells[]) => {
      highlightSourceIdRef.current = null;
      setHighlightedCells(cells);
      scheduleHighlightFade();
    },
    [scheduleHighlightFade],
  );

  const highlightCellsForEventRun = useCallback(
    (eventRunId: string) => {
      const sourceId = `event:${eventRunId}`;
      if (highlightSourceIdRef.current === sourceId) {
        clearHighlight();
        return;
      }
      highlightSourceIdRef.current = sourceId;
      setHighlightedCells(
        resolveHighlightCells(getSessionRunsForEventRun(eventRunId)),
      );
      scheduleHighlightFade();
    },
    [getSessionRunsForEventRun, clearHighlight, scheduleHighlightFade],
  );

  const highlightCellsForSessionRun = useCallback(
    (sessionRunId: string) => {
      const sourceId = `session:${sessionRunId}`;
      if (highlightSourceIdRef.current === sourceId) {
        clearHighlight();
        return;
      }
      const sessionRun = sessionRuns.find((r) => r.id === sessionRunId);
      if (!sessionRun) return;
      const entry = runLogEntries(
        sessionRuns.filter((r) => r.eventRunId === sessionRun.eventRunId),
      ).find((candidate) => candidate.run.id === sessionRunId);
      highlightSourceIdRef.current = sourceId;
      setHighlightedCells(
        resolveHighlightCells(entry ? entry.runs : [sessionRun]),
      );
      scheduleHighlightFade();
    },
    [sessionRuns, clearHighlight, scheduleHighlightFade],
  );

  // Escape clears the highlight. Listener is only attached while there is a
  // highlight to clear, and removed as soon as it isn't — so it never lingers
  // on `window` for the page's lifetime.
  useEffect(() => {
    if (highlightedCells.length === 0) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.defaultPrevented) return;
      if (e.key === "Escape") clearHighlight();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [highlightedCells.length, clearHighlight]);

  // Cancel any in-flight fade timers, scroll animations, and burst timers on
  // unmount, so none of them fires a state update or a scroll write against
  // an unmounted provider / removed DOM.
  useEffect(() => {
    return () => {
      cancelHighlightFadeTimers();
      cancelScrollAnimations(scrollRafIdsRef);
      cancelSessionRunsBurst();
    };
  }, [cancelHighlightFadeTimers, cancelSessionRunsBurst]);

  // Scroll the first highlighted cell into view whenever the highlight set
  // changes, mirroring the legacy LeftPanel behavior. A wildcard ("*",
  // row-wide) match scrolls to any cell in that row since no single column
  // element carries the wildcard as its `data-column-id`.
  //
  // Both axes are driven explicitly via `scrollElementIntoView` rather than
  // `Element.scrollIntoView` — see that function's docstring for why the
  // native version doesn't survive, on either axis, in this table. Vertical
  // always runs (skipped only when the cell is already fully visible
  // vertically). Horizontal only runs when the WHOLE selection — every
  // entry in `highlightedCells`, not just the first — targets exactly one
  // real column (a column rerun or a single-cell rerun, where "center on
  // this column" is meaningful); it's skipped for a wildcard row (no single
  // column to center on) and for a multi-column selection (a sample rerun,
  // a workpaper rerun — there's no single "the" column, so centering on
  // whichever one happened to sort first would be arbitrary), same as when
  // the cell is already fully visible horizontally.
  //
  // Checking only `firstHighlight.columnId === ALL_COLUMNS_WILDCARD` used
  // to be an equivalent, cheaper test, because every non-single-column run
  // resolved to the wildcard. That stopped holding once the backend started
  // materializing explicit `targetColumnIds` for every dispatch post-Phase-0
  // (`filter_unlocked_target_column_ids` no longer returns null, and it
  // excludes locked columns from the list it does return) — a sample rerun
  // now arrives here as several explicit per-column entries, never `"*"`,
  // so the old wildcard-only check let those slip through and incorrectly
  // center on their first column.
  useEffect(() => {
    if (highlightedCells.length === 0) return;
    const [firstHighlight] = highlightedCells;
    const isWildcardMatch = firstHighlight.columnId === ALL_COLUMNS_WILDCARD;
    const selector = isWildcardMatch
      ? `[data-source-row-id="${firstHighlight.sourceRowId}"]`
      : `[data-source-row-id="${firstHighlight.sourceRowId}"][data-column-id="${firstHighlight.columnId}"]`;
    const cellElement = document.querySelector(selector);
    if (!cellElement) return;
    const distinctColumns = new Set(
      highlightedCells.map((cell) => cell.columnId),
    );
    const isSingleColumnSelection =
      distinctColumns.size === 1 && !distinctColumns.has(ALL_COLUMNS_WILDCARD);
    const scrollTimer = setTimeout(() => {
      const verticalContainer = findScrollAncestor(cellElement, "vertical");
      if (
        verticalContainer &&
        !isFullyVisible(cellElement, verticalContainer, "vertical")
      ) {
        scrollElementIntoView(
          cellElement,
          verticalContainer,
          "vertical",
          scrollRafIdsRef,
        );
      }
      if (!isSingleColumnSelection) return;
      const horizontalContainer = findScrollAncestor(cellElement, "horizontal");
      if (
        !horizontalContainer ||
        isFullyVisible(cellElement, horizontalContainer, "horizontal")
      ) {
        return;
      }
      scrollElementIntoView(
        cellElement,
        horizontalContainer,
        "horizontal",
        scrollRafIdsRef,
      );
    }, 100);
    return () => {
      clearTimeout(scrollTimer);
      cancelScrollAnimations(scrollRafIdsRef);
    };
  }, [highlightedCells]);

  return (
    <SessionRunsContext.Provider
      value={{
        sessionRuns,
        errorRuns,
        eventRuns,
        loading,
        highlightedCells,
        isHighlightFading,
        addSessionRun,
        addSessionRuns,
        removeSessionRun,
        fetchSessionRuns,
        burstFetchSessionRuns,
        cancelSessionRunsBurst,
        getSessionRunsForSession,
        getSessionRunsForEventRun,
        highlightCells,
        highlightCellsForEventRun,
        highlightCellsForSessionRun,
        clearHighlight,
      }}
    >
      {children}
    </SessionRunsContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useSessionRunsContext() {
  const context = useContext(SessionRunsContext);
  if (context === undefined) {
    throw new Error(
      "useSessionRunsContext must be used within a SessionRunsProvider",
    );
  }
  return context;
}
